import { useNavigate } from "react-router-dom";
import { ClassificationBadge } from "./ClassificationBadge";
import { Leaf, AlertTriangle, XCircle } from "lucide-react";

const ICON = {
  GREEN: Leaf,
  YELLOW: AlertTriangle,
  RED: XCircle,
};

/**
 * MaterialCard
 *  - With `onClick`: behaves as a button and opens a modal (used in Materials Guide grid).
 *  - Without `onClick`: navigates to the canonical /materials/:slug page (used elsewhere).
 *  The card stays an `<a>` so right-click → "Open in new tab" still works and Google can crawl.
 */
export function MaterialCard({ material, onClick }) {
  const Icon = ICON[material.classification] || Leaf;
  const navigate = useNavigate();
  const href = `/materials/${material.slug}`;

  function handleClick(e) {
    // Allow native open-in-new-tab (ctrl/cmd/middle-click) and right-click context menus.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    if (onClick) onClick(material);
    else navigate(href);
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      className="text-left group bg-white border border-line rounded-2xl p-4 hover:shadow-lg transition-shadow w-full block cursor-pointer"
      data-testid={`material-card-${material.slug}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 shrink-0 ${material.classification === "GREEN" ? "text-leaf" : material.classification === "YELLOW" ? "text-amber" : "text-clay"}`} />
          <h3 className="font-serif text-lg leading-tight truncate" title={material.name}>{material.name}</h3>
        </div>
        <ClassificationBadge classification={material.classification} className="shrink-0" />
      </div>
      {material.short_summary && <p className="text-xs text-muted_text line-clamp-3">{material.short_summary}</p>}
    </a>
  );
}
